import { useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "react-query";
import { useLocation } from "react-router-dom";
import { Box, Paper } from "@mui/material";
import ProposalGenerationSplashScreen from "./components/ProposalGenerationSplashScreen";
import { StagesPanel } from "./components/StagesPanel";
import {
  useActiveDatasetIds,
  useChatStore,
  useSourceDataRecordIds,
} from "src/views/assistant/stores/ChatStore";
import { useSyncPersonaToStore } from "src/views/assistant/hooks/useSyncPersonaToStore";
import { useSetProposalGenerationRecentsConfig } from "src/views/assistant/hooks/useProductConfig";
import { useHistory } from "react-router-dom";
import { useCreateDialogue } from "src/reusable/Chat/ChatStreaming/services/api";
import { useProposalGenerationChat } from "./hooks/useProposalGenerationChat";
import { useProposalGenerationStore } from "./store/ProposalGenerationStore";
import {
  useCreateProposalGenerationRun,
  useGetProposalGenerationDialogueData,
  useGetProposalGenerationStages,
  useUpdateProposalGenerationDialogueData,
  useClearProposalGenerationStage,
  useRemoveStageFromRun,
  useAddStageToRun,
  getReportTypeDisplayName,
  stageDisplayConfig,
} from "./services/ProposalGenerationServices";
import { useGetStageConfig } from "./services/ProposalGenerationConfigService";
import {
  clearAllState,
  initializeStageModeState,
  type StageInitializationData,
} from "./utils/splashPageInitialization";
import { navigateToStage } from "./utils/navigationUtils";
import { useReportTemplateList } from "src/views/reports/ReportServices";
import { ReportType } from "src/interfaces/enums";
import { NucToast } from "src/reusable/components/Toast";
import { extractErrorMessage } from "./utils/errorMessageExtraction";
import {
  useWorkflowDialoguePersona,
  useWorkflowLeadPersona,
} from "src/views/products/workflow/hooks/useWorkflowLeadPersona";
import { PROPOSAL_GENERATION_PRODUCT } from "./proposalGenerationProduct";

const ProposalGenerationSplashPage = () => {
  // Parse query parameters for stage mode (runId and stageNumber)
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const stageRunId = searchParams.get("runId")
    ? Number(searchParams.get("runId"))
    : null;
  const stageNumber = searchParams.get("stageNumber")
    ? Number(searchParams.get("stageNumber"))
    : null;
  const isStageMode = stageRunId !== null && stageNumber !== null;

  // For stage mode, we need the dialogue ID which comes from the stages
  const { data: stages } = useGetProposalGenerationStages(
    isStageMode ? stageRunId : undefined,
  );

  const stageDialogueId = isStageMode
    ? stages?.find((s) => s.sequence_number === stageNumber)?.dialogue_id
    : undefined;

  // Load dialogue data for stage mode
  const { data: stageDialogueData } =
    useGetProposalGenerationDialogueData(stageDialogueId);

  // Fetch templates to resolve inherited template names
  const { data: templates } = useReportTemplateList(ReportType.GENERATIVE);

  // Mutation for updating dialogue data (stage mode only)
  const { mutate: updateDialogueData, isLoading: isUpdatingDialogueData } =
    useUpdateProposalGenerationDialogueData();
  // Sync persona from resource slots
  const { isLoading: isSyncingPersona, isError } = useSyncPersonaToStore({
    allowUrlSync: false,
    ignoreDialogueHistory: true,
  });

  // Pre-select the persona that will actually run, selected for real and
  // sent as chat_config_id. Initial mode: the review slot's resolution
  // (binding -> resource slot -> legacy generic). Stage mode: the stage
  // dialogue's own backend resolution (its creation-time persona).
  const { isLoading: isResolvingLeadPersona } = useWorkflowLeadPersona(
    PROPOSAL_GENERATION_PRODUCT,
    { enabled: !isStageMode },
  );
  const { isLoading: isResolvingDialoguePersona } = useWorkflowDialoguePersona(
    PROPOSAL_GENERATION_PRODUCT,
    stageDialogueId,
    { enabled: isStageMode },
  );
  // Kicking off before resolution lands would send chat_config_id: null, and
  // the backend's null path resolves the generic slot rather than this slot's
  // ladder — a different persona than the one the button is about to show.
  const isResolvingPersona =
    isResolvingLeadPersona || isResolvingDialoguePersona;

  const {
    config: chatConfig,
    setInitialUserMessage,
    isChatReady,
  } = useChatStore();

  // Get active dataset IDs from store's computed selector
  const activeDatasetIds = useActiveDatasetIds();

  // Get source data record IDs for document-scoped search
  const sourceDataRecordIds = useSourceDataRecordIds();
  const history = useHistory();
  const {
    setRunId: setProposalGenerationRunId,
    inputFiles,
    selectedTemplate,
    setReportType,
    setSequenceNumber,
    setStageReportPrefix,
    setStageReportSuffixLabel,
  } = useProposalGenerationStore();

  // Effective stage config (fallback when the run has no snapshot yet)
  const { data: stageConfig } = useGetStageConfig();

  // Mirror the run-level prefix/suffix snapshot into the store so the splash
  // screen derives display names from the run (not the live config), matching
  // the chat sidebar. All stages in a run share these values.
  useEffect(() => {
    setStageReportPrefix(stages?.[0]?.stage_report_prefix ?? null);
    setStageReportSuffixLabel(stages?.[0]?.stage_report_suffix_label ?? null);
  }, [stages, setStageReportPrefix, setStageReportSuffixLabel]);

  // Mutation for creating a proposal generation run
  const { mutateAsync: createProposalRun, isLoading: isCreatingRun } =
    useCreateProposalGenerationRun();

  // Get chat hook methods for the splash screen UI
  // Build datasets from store's computed selector (matching AtomAssist pattern)
  const datasetsToUse = activeDatasetIds.map((id) => ({ id }));

  const {
    terminateConnection,
    isConnected: isChatConnected,
    isConnectionError: isChatConnectionError,
    streamError: chatStreamError,
    isEnded: isChatEnded,
    isLoading: isChatLoading,
  } = useProposalGenerationChat({
    modelId: chatConfig?.modelId,
    modelVersionNum: chatConfig?.modelVersionNum,
    datasetsForSearchContext: datasetsToUse,
    sourceDataRecordIds:
      sourceDataRecordIds.length > 0 ? sourceDataRecordIds : undefined,
    numSearchResults: chatConfig?.topSearchResults || 5,
    product: "proposal-generation",
  });

  const {
    mutateAsync: createDialogue,
    isLoading: isCreatingDialogue,
    error: dialogueError,
    isError: isDialogueError,
  } = useCreateDialogue();

  // Unified validation function for both initial and stage modes
  const validateBeforeGeneration = (): boolean => {
    // Check if chat is ready (only required for initial mode)
    if (!isChatReady()) {
      NucToast({
        message: "Chat is not ready. Please wait for configuration to load.",
        type: "error",
      });
      return false;
    }

    // isChatReady only asserts a config exists, and the base config carries no
    // chatConfigId, so it passes while the persona is still resolving.
    if (isResolvingPersona) {
      NucToast({
        message:
          "Still resolving which agent runs this. Try again in a moment.",
        type: "error",
      });
      return false;
    }

    // Validate that we have either RFP files or datasets
    if (inputFiles.length === 0 && activeDatasetIds.length === 0) {
      console.error(
        "[ProposalGenerationSplashPage] Missing both inputFiles and datasets",
      );
      NucToast({
        message: "Please upload an RFP document or select a dataset first.",
        type: "error",
        hideProgressBar: false,
      });
      return false;
    }

    // Validate template selection
    if (!selectedTemplate) {
      console.error("[ProposalGenerationSplashPage] Missing selectedTemplate");
      NucToast({
        message: "Please select a template first.",
        type: "error",
        hideProgressBar: false,
      });
      return false;
    }

    // Validate artifact IDs if we have RFP files
    if (inputFiles.length > 0) {
      const artifactIds = inputFiles
        .filter((file) => file.id)
        .map((file) => file.id!);

      if (artifactIds.length === 0) {
        // Check if files are still processing
        const filesWithoutIds = inputFiles.filter((f) => !f.id);
        console.warn(
          "[ProposalGenerationSplashPage] No artifact IDs found. Files without IDs:",
          filesWithoutIds.map((f) => ({
            name: f.name,
            processingStatus: f.processingStatus,
            sdrId: f.sdrId,
          })),
        );

        NucToast({
          message:
            "Files are still processing. Please wait for processing to complete.",
          type: "error",
          hideProgressBar: false,
        });
        return false;
      }
    }

    return true;
  };

  const handleGenerateReport = async (
    userMessageText?: string,
  ): Promise<boolean> => {
    // Handle stage mode - update dialogue data and navigate to chat
    if (isStageMode && stageRunId && stageDialogueId && stageDialogueData) {
      console.log(
        "[ProposalGenerationSplashPage] Stage Mode - Updating dialogue for stage",
        stageNumber,
        "dialogueId:",
        stageDialogueId,
      );

      // Run unified validation before proceeding
      if (!validateBeforeGeneration()) {
        return Promise.resolve(false);
      }

      try {
        // Update the dialogue with selected template and datasets
        // Wrap in Promise to wait for mutation completion
        return new Promise<boolean>((resolve) => {
          updateDialogueData(
            {
              dialogueId: stageDialogueId,
              payload: {
                // @ts-expect-error[reportOptionalMemberAccess]
                template_id: selectedTemplate.id,
                dataset_ids: activeDatasetIds,
                input_artifact_ids: inputFiles
                  .filter((file) => file.id)
                  .map((file) => file.id!),
                report_sdr_id: null,
              },
            },
            {
              onSuccess: () => {
                console.log(
                  "[ProposalGenerationSplashPage] Successfully updated stage dialogue",
                );

                // Set initial message for chat
                const reportDisplayName = getReportTypeDisplayName(
                  stageDialogueData.report_type,
                  stageNumber,
                  stageDisplayConfig(
                    stages?.[0]?.stage_report_prefix,
                    stages?.[0]?.stage_report_suffix_label,
                    stageConfig?.effective,
                  ),
                );
                setInitialUserMessage(
                  userMessageText?.trim() || `Generate a ${reportDisplayName}`,
                );

                // Navigate to chat page with stage run and dialogue IDs
                history.push(
                  `/proposal-generation/chat/${stageRunId}/${stageDialogueId}`,
                );

                resolve(true);
              },
              onError: (error: unknown) => {
                console.error(
                  "[ProposalGenerationSplashPage] Failed to update dialogue:",
                  error,
                );
                const errorMessage = extractErrorMessage(
                  error,
                  "Failed to update stage configuration",
                );
                NucToast({
                  message: errorMessage,
                  type: "error",
                });
                resolve(false);
              },
            },
          );
        });
      } catch (error) {
        const errorMessage = extractErrorMessage(
          error,
          "Failed to configure stage",
        );
        NucToast({
          message: `${errorMessage}`,
          type: "error",
        });
        return Promise.resolve(false);
      }
    }

    // Run unified validation before proceeding
    if (!validateBeforeGeneration()) {
      return Promise.resolve(false);
    }

    const userMessage = userMessageText?.trim() || "Generate a Review Report";

    try {
      const { getOrCreateDialogue } = useProposalGenerationStore.getState();

      // Extract artifact IDs for initial mode run creation
      const artifactIds = inputFiles
        .filter((file) => file.id)
        .map((file) => file.id!);

      const effectiveDialogueId = await getOrCreateDialogue(async () => {
        const id = await createDialogue({
          conversation: {
            model_id: chatConfig?.modelId ?? null,
            model_version_number: chatConfig?.modelVersionNum ?? null,
            search_datasets: activeDatasetIds, // Use computed dataset IDs from store
            n_search_results: chatConfig?.topSearchResults || 5,
            model_inference_data: {
              infer_data: JSON.stringify([]),
              stream: true,
              streaming_response_required: true,
              text_gen_model_params: chatConfig?.chatControlMetadata,
            },
            stream: true,
            tool_calling: true,
            chat_config_id: chatConfig?.chatConfigId || null,
            product: "proposal-generation",
          },
        });

        console.log(
          "[ProposalGenerationSplashPage] Created dialogue with ID:",
          id,
        );
        return id;
      });

      const effectiveRunId = await createProposalRun({
        dialogue_id: effectiveDialogueId,
        input_artifact_ids: artifactIds,
        dataset_ids: activeDatasetIds,
        // @ts-expect-error[reportOptionalMemberAccess]
        template_id: selectedTemplate.id,
        report_type: "review report",
        sequence_number: 1,
      });
      setProposalGenerationRunId(effectiveRunId);

      // Store report type and sequence in store
      setReportType("review report");
      setSequenceNumber(1);

      console.log(
        "[ProposalGenerationSplashPage] Created proposal generation run",
        effectiveRunId,
      );

      // Store the user message in the chat store for the chat page to retrieve
      setInitialUserMessage(userMessage);

      // Navigate to chat page with both run ID and dialogue ID
      history.push(
        `/proposal-generation/chat/${effectiveRunId}/${effectiveDialogueId}`,
      );

      return Promise.resolve(true);
    } catch (error) {
      const errorMessage = extractErrorMessage(
        error,
        "Failed to start report generation",
      );
      NucToast({
        message: errorMessage,
        type: "error",
        hideProgressBar: false,
      });
      return Promise.resolve(false);
    }
  };

  const queryClient = useQueryClient();

  // Set product configuration with custom URL builder for proposal generation
  useSetProposalGenerationRecentsConfig(queryClient);

  // Mutation hooks for stage management
  const clearStageMutation = useClearProposalGenerationStage();
  const removeStageMutation = useRemoveStageFromRun();
  const addStageMutation = useAddStageToRun();

  // Handler for clicking stages in the sidebar
  const handleStageClick = useCallback(
    (targetDialogueId: number, stageNumber?: number) => {
      navigateToStage(
        history,
        stageRunId || undefined,
        stages,
        targetDialogueId,
        stageNumber,
        "[ProposalGenerationSplashPage]",
      );
    },
    [history, stageRunId, stages],
  );

  // Handler for clearing a stage
  const handleClearStage = useCallback(
    (targetDialogueId: number) => {
      if (!stageRunId) return;
      const stage = stages?.find((s) => s.dialogue_id === targetDialogueId);
      clearStageMutation.mutate(
        { dialogueId: targetDialogueId, runId: stageRunId },
        {
          onSuccess: () => {
            if (stage?.sequence_number) {
              history.push(
                `/proposal-generation?runId=${stageRunId}&stageNumber=${stage.sequence_number}`,
              );
            }
          },
          onError: () => {
            NucToast({ message: "Failed to reset stage", type: "error" });
          },
        },
      );
    },
    [stageRunId, stages, clearStageMutation, history],
  );

  // Handler for removing a stage
  const handleRemoveStage = useCallback(
    (targetDialogueId: number) => {
      if (!stageRunId) return;
      removeStageMutation.mutate(
        { runId: stageRunId, dialogueId: targetDialogueId },
        {
          onSuccess: () => {
            // Only navigate if removing the stage we're currently viewing
            if (targetDialogueId === stageDialogueId) {
              const reviewStage = stages?.find((s) => s.sequence_number === 1);
              if (reviewStage) {
                history.push(
                  `/proposal-generation/chat/${stageRunId}/${reviewStage.dialogue_id}`,
                );
              }
            }
          },
          onError: () => {
            NucToast({ message: "Failed to remove stage", type: "error" });
          },
        },
      );
    },
    [stageRunId, stageDialogueId, stages, removeStageMutation, history],
  );

  // Handler for adding a stage
  const handleAddStage = useCallback(
    (reportType: string) => {
      if (!stageRunId) return;
      addStageMutation.mutate(
        {
          runId: stageRunId,
          payload: { report_type: reportType },
        },
        {
          onError: () => {
            NucToast({ message: "Failed to add stage", type: "error" });
          },
        },
      );
    },
    [stageRunId, addStageMutation],
  );

  // Track which stage has been initialized to avoid re-running on dependency changes
  // but still allow re-initialization when navigating to a different stage
  const prevStageKeyRef = useRef<string>("");

  // Clear state once on mount (non-stage mode only)
  useEffect(() => {
    if (!isStageMode) {
      clearAllState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stage mode initialization - runs when all required data is available
  useEffect(() => {
    if (!isStageMode || !stageDialogueData || !templates) {
      return;
    }

    const stageKey = `${stageRunId}-${stageNumber}`;
    if (prevStageKeyRef.current === stageKey) {
      return;
    }

    prevStageKeyRef.current = stageKey;
    let isMounted = true;

    const initData: StageInitializationData = {
      isStageMode,
      stageRunId,
      stageNumber,
      stageDialogueId,
      stageDialogueData,
      templates,
    };

    initializeStageModeState(initData).then((result) => {
      if (!isMounted) return;
      if (!result.success) {
        NucToast({
          message: `Failed to initialize stage: ${result.errors.join(", ")}`,
          type: "error",
        });
      }
    });
    return () => {
      isMounted = false;
    };
  }, [
    isStageMode,
    stageRunId,
    stageNumber,
    stageDialogueId,
    stageDialogueData,
    templates,
  ]);

  // Show loading only when actually syncing persona in initial mode
  if (!isStageMode && isSyncingPersona) {
    return null; // Or a loading component if you want to show one
  }

  // Handle loading state for stage mode
  if (isStageMode && (!stageDialogueData || !stages)) {
    return null; // Loading stage data
  }

  if (!isStageMode && isError) {
    return (
      <ProposalGenerationSplashScreen
        chatStreamError="Failed to load configuration. Please try again later."
        isChatConnectionError={true}
        isChatConnected={isChatConnected}
        isChatLoading={isChatLoading}
        isChatEnded={isChatEnded}
        terminateConnection={terminateConnection}
        onGenerateReport={async () => false}
      />
    );
  }

  // Render stage mode with optional sidebar
  if (isStageMode) {
    const hasMultipleStages = (stages?.length || 0) > 1;
    const isStageMutating =
      clearStageMutation.isLoading ||
      removeStageMutation.isLoading ||
      addStageMutation.isLoading;

    return (
      <Box sx={{ height: "100vh", display: "flex", gap: 2, py: 3, px: 3 }}>
        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          <ProposalGenerationSplashScreen
            chatStreamError=""
            isChatConnectionError={isChatConnectionError || isDialogueError}
            isChatConnected={isChatConnected}
            isChatLoading={isChatLoading || isUpdatingDialogueData}
            isChatEnded={isChatEnded}
            terminateConnection={terminateConnection}
            onGenerateReport={handleGenerateReport}
          />
        </Box>

        {/* Right Sidebar: Stages - only show if there are multiple stages */}
        {hasMultipleStages && (
          <Paper sx={{ width: 300, flexShrink: 0, p: 2, overflowY: "auto" }}>
            <StagesPanel
              stages={stages || []}
              currentDialogueId={stageDialogueId || 0}
              onStageClick={handleStageClick}
              onClearStage={handleClearStage}
              onRemoveStage={handleRemoveStage}
              onAddStage={handleAddStage}
              isMutating={isStageMutating}
            />
          </Paper>
        )}
      </Box>
    );
  }

  return (
    <ProposalGenerationSplashScreen
      chatStreamError={
        chatStreamError ||
        (isDialogueError ? (dialogueError as Error)?.message : "")
      }
      isChatConnectionError={isChatConnectionError || isDialogueError}
      isChatConnected={isChatConnected}
      isChatLoading={isChatLoading || isCreatingDialogue || isCreatingRun}
      isChatEnded={isChatEnded}
      terminateConnection={terminateConnection}
      onGenerateReport={handleGenerateReport}
    />
  );
};

export default ProposalGenerationSplashPage;
